import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { Link, useLocation } from "react-router-dom";
import "../styles/Navbar.scss";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SchoolIcon from "@mui/icons-material/School";

import FolderIcon from "@mui/icons-material/Folder";
import KeyboardDoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { checkIsAdmin } from "../helper/authUtils";
import { clearPageIdCookies } from "../helper/cookieUtils";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import TooltipComponent from "./tooltipComponent";
import BoltIcon from "@mui/icons-material/Bolt";
import QuizIcon from "@mui/icons-material/Quiz";
import ArticleIcon from "@mui/icons-material/Article";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import BookIcon from "@mui/icons-material/Book";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import edluminaHorizontalLogo from "../assets/png/edluminaHorizontalLogo.png";
import edluminaLogo from "../assets/png/edluminaLogo.png";
import { getPendingOverallCourseTestCount } from "../helper/apicalls/overallTest";

const Navbar = ({ collapsed: collapsedProp, onToggleCollapse }) => {
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isControlled = typeof collapsedProp === "boolean";
  const collapsed = isControlled ? collapsedProp : internalCollapsed;
  const [isLearningZoneExpanded, setIsLearningZoneExpanded] = useState(false);
  const [pendingTestCount, setPendingTestCount] = useState(0);

  const toggleCollapsed = () => {
    if (typeof onToggleCollapse === "function") {
      onToggleCollapse();
    } else {
      setInternalCollapsed((c) => !c);
    }
  };

  const courseKey = Cookies.get("courseKey");

  useEffect(() => {
    let isMounted = true;
    const fetchPending = async () => {
      try {
        const token = Cookies.get("accessToken");
        if (!token || !courseKey) {
          if (isMounted) setPendingTestCount(0);
          return;
        }
        const data = await getPendingOverallCourseTestCount(courseKey);
        const value =
          typeof data === "number"
            ? data
            : Number(
                data?.pending_homework ?? data?.count ?? data?.pending ?? 0,
              );
        if (isMounted)
          setPendingTestCount(Number.isFinite(value) ? Math.round(value) : 0);
      } catch (e) {
        if (isMounted) setPendingTestCount(0);
      }
    };
    fetchPending();
    return () => {
      isMounted = false;
    };
  }, [courseKey]);

  const navItems = [
    {
      id: 1,
      text: "Dashboard",
      path: "/dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: 2,
      text: "Course",
      path: courseKey ? `/courses/${courseKey}` : "/courses",
      icon: <SchoolIcon />,
    },
    {
      id: 3,
      text: "Homeworks",
      path: "/homeworks",
      icon: <AssignmentIcon />,
    },
    {
      id: 4,
      text: "Guides and Materials",
      path: courseKey
        ? `/overall-materials/${courseKey}`
        : "/overall-materials",
      icon: <BookIcon />,
    },
    {
      id: 5,
      text: "Current Affairs",
      path: courseKey ? `/current-affairs/${courseKey}` : "/current-affairs",
      icon: <ArticleIcon />,
    },
    {
      id: 6,
      text: "Videos",
      path: courseKey ? `/videos/${courseKey}` : "/videos",
      icon: <VideoFileIcon />,
    },
    {
      id: 7,
      text: "Learning Zone",
      path: "/active-recall",
      icon: <LocalLibraryIcon />,
    },
    {
      id: 8,
      text: "Materials",
      path: courseKey ? `/materials/${courseKey}` : "/materials",
      icon: <FolderIcon />,
    },
    {
      id: 9,
      text: "Question Bank",
      path: courseKey ? `/question-bank/${courseKey}` : "/question-bank",
      icon: <QuizIcon />,
    },
  ].filter((item) => {
    if (checkIsAdmin()) {
      return item.text === "Course";
    }
    return true;
  });

  return (
    <>
      <nav className={`navbar ${collapsed ? "collapsed" : ""}`}>
        <div className="navbar-container">
          <div className={`navbar-brand ${collapsed ? "navbar-collapse" : ""}`}>
            <Link to="/dashboard" className="navbar-logo" onClick={() => clearPageIdCookies()}>
              {collapsed ? (
                <img
                  src={edluminaLogo}
                  className="navbar-logo-img-collapsed"
                  alt="Edlumina logo"
                />
              ) : (
                <img
                  src={edluminaHorizontalLogo}
                  className="navbar-logo-img"
                  alt="Edlumina logo"
                />
              )}
            </Link>
          </div>

          <div className="navbar-menu custom-scrollbar">
            {navItems.map((item) => {
              const isActive =
                (item.text === "Materials" &&
                  location.pathname.startsWith("/materials")) ||
                (item.text === "Materials" &&
                  location.pathname.startsWith("/materials-folder")) ||
                (item.text === "Question Bank" &&
                  location.pathname.startsWith("/question-bank")) ||
                (item.text === "Question Bank" &&
                  location.pathname.startsWith("/question-bank-folder")) ||
                (item.text === "Learning Zone" &&
                  (location.pathname.startsWith("/gamification") ||
                    location.pathname.startsWith("/active-recall") ||
                    location.pathname.startsWith("/adaptive-learning"))) ||
                (item.text === "Current Affairs" &&
                  location.pathname.startsWith("/current-affairs")) ||
                (item.text !== "Materials" &&
                  item.text !== "Question Bank" &&
                  item.text !== "Learning Zone" &&
                  item.text !== "Current Affairs" &&
                  location.pathname.startsWith(item.path));

              const isActiveRecallActive =
                location.pathname.startsWith("/active-recall");
              const isAdaptiveLearningActive =
                location.pathname.startsWith("/adaptive-learning");

              if (item.text === "Learning Zone") {
                return (
                  <React.Fragment key={item.id}>
                    <div className="navbar-learning-zone-header">
                      <Link
                        to={item.path}
                        className={`navbar-link ${isActive ? "active" : ""}`}
                        onClick={() => {
                          setIsLearningZoneExpanded((prev) => !prev);
                          clearPageIdCookies();
                        }}
                      >
                        <span className="nav-icon">{item.icon}</span>
                        <div className="expanded-wrapper">
                          <TooltipComponent className="nav-text">
                            {item.text}
                          </TooltipComponent>
                          <button
                            type="button"
                            className="navbar-learning-zone-toggle"
                          >
                            {isLearningZoneExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </button>
                        </div>
                      </Link>
                    </div>
                    {isLearningZoneExpanded && (
                      <>
                        {/* <Link
                          key="learning-zone-skill-arena"
                          to="/gamification"
                          className={`navbar-link navbar-link-child ${isSkillArenaActive ? "active" : ""}`}
                        >
                          <span className="nav-icon">
                            <SportsEsportsIcon fontSize="small" />
                          </span>
                          <TooltipComponent className="nav-text">
                            Skill arena
                          </TooltipComponent>
                        </Link> */}
                        <Link
                          key="learning-zone-active-recall"
                          to="/active-recall"
                          className={`navbar-link navbar-link-child ${isActiveRecallActive ? "active" : ""}`}
                          onClick={() => clearPageIdCookies()}
                        >
                          <span className="nav-icon">
                            <BoltIcon fontSize="small" />
                          </span>
                          <TooltipComponent className="nav-text">
                            Active recall
                          </TooltipComponent>
                        </Link>
                        <Link
                          key="learning-zone-adaptive-learning"
                          to="/adaptive-learning"
                          className={`navbar-link navbar-link-child ${isAdaptiveLearningActive ? "active" : ""}`}
                          onClick={() => clearPageIdCookies()}
                        >
                          <span className="nav-icon">
                            <AutoGraphIcon fontSize="small" />
                          </span>
                          <TooltipComponent className="nav-text">
                            Adaptive learning
                          </TooltipComponent>
                        </Link>
                      </>
                    )}
                  </React.Fragment>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`navbar-link ${isActive ? "active" : ""}`}
                  onClick={() => {
                    if (item.text !== "Course") {
                      clearPageIdCookies();
                    }
                  }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <TooltipComponent className="nav-text">
                    {item.text}
                  </TooltipComponent>
                  {item.text === "Homeworks" && pendingTestCount > 0 && (
                    <span className="nav-badge">
                      {pendingTestCount > 99 ? "99+" : pendingTestCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="navbar-actions">
            <button
              type="button"
              className={`navbar-btn ghost ${collapsed ? "collapse-icon-active" : ""}`}
              onClick={toggleCollapsed}
            >
              {collapsed ? (
                <KeyboardDoubleArrowRight fontSize="small" />
              ) : (
                <>
                  <TooltipComponent>Collapse</TooltipComponent>
                  <KeyboardDoubleArrowLeft fontSize="small" />
                </>
              )}
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
